"use client"
import { motion } from "framer-motion"
import {
  Building2,
  BarChart3,
  Target,
  CheckCircle2,
  LineChart,
  TrendingUp,
  Calendar,
  PieChart,
  Lock,
  Award,
  ChevronDown,
  ChevronUp,
  Sparkles,
  Shield,
  Zap,
  GraduationCap,
  ArrowRight,
} from "lucide-react"
import { useState } from "react"
import Layout from "../../components/layout"
import Customers from "../../components/customers/Customers"

const Landing2 = () => {
  const [openFaq, setOpenFaq] = useState(null)

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index)
  }
  const testimonials = [
    {
      //   name: "PE Firm",

      content:
        "We use Venture Intelligence products and services on a day-to-day basis for various kinds of research. These include looking up aggregate and deal-by-deal information on the latest as well as historical investments and exits, and also slice and dice the data using various filters.",
    },
    {
      content: " Venture Intelligence actively captures Exit of PE-VC Funds with their return multiple and IRR.",
    },
    {
      //   name: "Investment Bank",

      content:
        "Venture Intelligence products—including the morning and evening Deal Digest Newsletter—help us remain up-to-date with industry happenings, monitor deals our peers are doing, get alerted about new transaction opportunities, track people movement, etc.",
    },
    {
      //   name: "VC Firm",

      content:
        "We use the in-built PE/VC Directory in the database to scan for the PE/VC firms who have been active in a particular sector or stage. The various filter options help us zero down on the firm we can reach for follow-on investments/buyouts/co-investment opportunities.",
    },
  ]

  const faqs = [
    {
      question: "What kind of data does Venture Intelligence provide?",
      answer:
        "We offer comprehensive data on Private Equity/Venture Capital Investments and Exits, Mergers and Acquisitions, and Private Company Financials. Each segment is available as a standalone database or bundled as part of our full offering. Our Morning and Evening Newsletters provide timely updates on key developments across the PE-VC ecosystem.",
    },
    {
      question: "Who uses Venture Intelligence?",
      answer:
        "Our clients include top-tier PE/VC firms, investment banks, consulting firms, corporate strategy teams, government bodies, and academic researchers.",
    },
    {
      question: "How frequently is the data updated?",
      answer:
        "Our data is updated daily. Deals are sourced from news publications and advisor submissions. Financial and valuation metrics are added as they become available through MCA filings, ensuring timely and accurate insights.",
    },
    {
      question: "Can I access historical data on PE/VC and M&A transactions?",
      answer:
        "Yes. Our PE/VC database includes investment data dating back to 1998, exits since 2004, and M&A data also since 2004—offering a rich historical view of the Indian deal landscape.",
    },
    {
      question: "Do you cover global data for the above sections?",
      answer:
        "No, our focus is on Indian private company data. However, we include relevant public company data in India where applicable within our databases.",
    },
    {
      question: "What does the PE-VC Deals database cover?",
      answer:
        "It includes PE-VC investments with detailed metrics such as pre- and post-money valuation, enterprise value, and multiples (revenue, EBITDA, PAT). It also tracks PE-VC exits with return multiples, IPOs, angel deals, and incubation activity. The database features an investor directory with contact details, portfolio companies, and advisor transaction history.",
    },
    {
      question: "How can I request a demo or trial access?",
      answer:
        "You can request a personalized demo by filling out the form on our website. Our team will reach out to schedule a walkthrough based on your requirements.",
    },
    {
      question: "What subscription plans are available?",
      answer:
        "We offer flexible subscription plans tailored for investment firms, corporates, advisory firms, and academic institutions. Contact us for a customized quote.",
    },
    {
      question: "Is there a student or academic license option?",
      answer:
        "Yes, we provide academic access for universities and research institutions. Please get in touch to learn more about eligibility and pricing.",
    },
    {
      question: "Can I export data from the platform?",
      answer:
        "Yes, subscribers can export data in various formats for analysis and reporting, subject to their subscription tier.",
    },
    {
      question: "Is there an API available for data integration?",
      answer:
        "Yes, we provide API access to integrate our data with your internal tools or dashboards, enabling seamless workflows.",
    },
    {
      question: "Can I access the platform on mobile devices?",
      answer:
        "Yes, the platform works on all modern web browsers, including mobile. Some datasets are also accessible via WhatsApp for on-the-go convenience.",
    },
    {
      question: "What support options are available for subscribers?",
      answer:
        "Subscribers receive dedicated support via email and phone, onboarding help, and periodic training sessions to ensure effective usage.",
    },
    {
      question: "Has Venture Intelligence been cited in the media?",
      answer:
        "Yes, our data and insights are regularly featured in leading publications including The Wall Street Journal, Economic Times, and Mint.",
    },
    {
      question: "What are some notable events hosted by Venture Intelligence?",
      answer:
        "We host the APEX PE-VC Conference (since 2007) and the PE-VC Conclave (since 2022), which bring together top investors, startups, bankers, and advisors from across the ecosystem.",
    },
  ]

  const clientTypes = [
    {
      icon: <Building2 className="w-8 h-8" />,
      title: "PE-VC / Strategic Investment / Advisory Firm?",
      description: "Get comprehensive market intelligence and deal insights tailored for investment professionals",
      actionText: "Request a Demo Here",
      link: "https://www.ventureintelligence.com/trial2.htm",
      gradient: "from-blue-600 to-cyan-600",
      bgGradient: "from-blue-50 to-cyan-50",
      hoverGradient: "from-blue-700 to-cyan-700",
    },
    {
      icon: <TrendingUp className="w-8 h-8" />,
      title: "Company Seeking to Raise Capital?",
      description: "Access investor databases to accelerate your fundraising journey",
      actionText: "Click Here",
      link: "https://www.ventureintelligence.com/entrepreneurs",
      gradient: "from-orange-500 to-amber-500",
      bgGradient: "from-orange-50 to-amber-50",
      hoverGradient: "from-orange-600 to-amber-600",
    },
    {
      icon: <GraduationCap className="w-8 h-8" />,
      title: "Student?",
      description:
        "Involve someone authorized to make a purchase decision (eg: Librarian / Professor) and then request access",
      actionText: "Request a Demo",
      link: "https://www.ventureintelligence.com/trial2.htm",
      gradient: "from-blue-600 to-cyan-600",
      bgGradient: "from-blue-50 to-cyan-50",
      hoverGradient: "from-blue-700 to-cyan-700",
    },
  ]

  return (
    <Layout>
      <div className="min-h-screen bg-gray-50">
        {/* Hero Section - Compact */}
        <section className="relative overflow-hidden bg-gradient-to-br from-gray-900 via-slate-800 to-zinc-900 text-white py-12 lg:py-16">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg%20width=%2260%22%20height=%2260%22%20viewBox=%220%200%2060%2060%22%20xmlns=%22http://www.w3.org/2000/svg%22%3E%3Cg%20fill=%22none%22%20fillRule=%22evenodd%22%3E%3Cg%20fill=%22%23ffffff%22%20fillOpacity=%220.1%22%3E%3Ccircle%20cx=%2230%22%20cy=%2230%22%20r=%222%22/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')]"></div>
          </div>

          <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center mb-10"
            >
              <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-4 py-2 mb-6">
                <Sparkles className="w-4 h-4 text-amber-400" />
                <span className="text-sm font-medium">India's Leading Private Company Intelligence</span>
              </div>

              <h1 className="text-3xl md:text-5xl font-bold mb-4 leading-tight">
                <span className="text-blue-100 ">Venture Intelligence</span>
              </h1>
              <h2 className="text-lg md:text-xl font-semibold text-amber-600 mb-6">
                Your Gateway to India's Private Equity & Venture Capital Landscape
              </h2>
              <p className="text-base text-blue-200 max-w-3xl mx-auto leading-relaxed">
                Unlock opportunities, make informed decisions, and stay ahead in the dynamic world of private company
                transactions, financials, and valuations.
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[
                {
                  icon: <Building2 className="w-8 h-8" />,
                  title: "Company Data",
                  description:
                    "Comprehensive insights into private companies across India with detailed financial metrics",
                  gradient: "from-blue-500 to-cyan-500",
                },
                {
                  icon: <BarChart3 className="w-8 h-8" />,
                  title: "Transactions",
                  description: "Detailed PE, VC & M&A deals with complete transaction history and market trends",
                  gradient: "from-purple-500 to-pink-500",
                },
                {
                  icon: <LineChart className="w-8 h-8" />,
                  title: "Valuations",
                  description: "Pre-Money, Post Money & Enterprise valuations for informed investment decisions",
                  gradient: "from-amber-500 to-orange-500",
                },
              ].map((feature, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 + index * 0.1, duration: 0.5 }}
                  className="group relative"
                >
                  <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-xl p-6 hover:bg-white/15 transition-all duration-300 hover:scale-105">
                    <div className={`inline-flex p-3 rounded-lg bg-gradient-to-r ${feature.gradient} mb-4 shadow-lg`}>
                      <div className="text-white">{feature.icon}</div>
                    </div>
                    <h3 className="text-lg font-bold mb-3 text-white">{feature.title}</h3>
                    <p className="text-blue-200 text-sm leading-relaxed">{feature.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Trusted Partner Section - Compact */}
        <section className="relative py-12 lg:py-16 bg-gradient-to-br from-white via-blue-50/30 to-indigo-50/50">
          <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center mb-10"
            >
              <div className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-full px-4 py-2 mb-6 shadow-lg">
                <Shield className="w-4 h-4" />
                <span className="text-sm font-medium">Trusted Since 2002</span>
              </div>

              <h2 className="text-2xl md:text-4xl font-bold text-gray-900 mb-4">
                Your <span className="bg-gradient-to-r text-gray-900 bg-clip-text ">Trusted Partner</span>
              </h2>
              <h3 className="text-lg md:text-xl font-semibold text-amber-600 mb-6">In Private Company Intelligence</h3>
              <p className="text-base text-gray-600 max-w-3xl mx-auto leading-relaxed">
                Launched in 2002, the VI service is India's leading source for private company financials, transactions
                (PE, VC, M&A), and valuations, trusted by thousands of professionals worldwide.
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[
                {
                  icon: <CheckCircle2 className="w-8 h-8" />,
                  title: "Efficient Formats",
                  description: "Spot the right business opportunities with our streamlined data presentation",
                  color: "from-green-500 to-emerald-500",
                  bgColor: "from-green-50 to-emerald-50",
                },
                {
                  icon: <Target className="w-8 h-8" />,
                  title: "Wide Usage",
                  description: "Trusted by industry practitioners, entrepreneurs, and decision makers",
                  color: "from-blue-500 to-cyan-500",
                  bgColor: "from-blue-50 to-cyan-50",
                },
                {
                  icon: <TrendingUp className="w-8 h-8" />,
                  title: "Media Trust",
                  description: "Recognized as the go-to source for educational institutions and research",
                  color: "from-purple-500 to-pink-500",
                  bgColor: "from-purple-50 to-pink-50",
                },
              ].map((feature, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.2 + index * 0.1, duration: 0.5 }}
                  className="group relative"
                >
                  <div
                    className={`bg-gradient-to-br ${feature.bgColor} border border-white/60 rounded-xl p-6 hover:scale-105 transition-all duration-300`}
                  >
                    <div className={`inline-flex p-3 rounded-lg bg-gradient-to-r ${feature.color} mb-4 shadow-lg`}>
                      <div className="text-white">{feature.icon}</div>
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 mb-3">{feature.title}</h3>
                    <p className="text-gray-700 text-sm leading-relaxed">{feature.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Differentiators Section - Compact */}
        <section className="relative py-12 lg:py-16 bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50">
          <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Heading */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center mb-10"
            >
              <div className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-full px-4 py-2 mb-6 shadow-lg">
                <Zap className="w-4 h-4" />
                <span className="text-sm font-medium">What Sets Us Apart</span>
              </div>

              <h2 className="text-2xl md:text-4xl font-bold text-gray-900 mb-4">
                Our <span className=" text-gray-900">Differentiators</span>
              </h2>
              <h3 className="text-lg md:text-xl font-semibold text-gray-700 mb-6">Why Choose Us?</h3>
            </motion.div>

            {/* First 4 points in grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-4xl mx-auto mb-8">
              {[
                {
                  icon: <Calendar className="w-6 h-6" />,
                  title: "On ground presence since 2002",
                  gradient: "from-blue-500 to-indigo-500",
                },
                {
                  icon: <PieChart className="w-6 h-6" />,
                  title: "Transaction Valuations Multiples",
                  gradient: "from-green-500 to-emerald-500",
                },
                {
                  icon: <TrendingUp className="w-6 h-6" />,
                  title: "Return Multiples of PE/VC Deals",
                  gradient: "from-purple-500 to-pink-500",
                },
                {
                  icon: <Lock className="w-6 h-6" />,
                  title: "Deals unavailable in public domain",
                  gradient: "from-red-500 to-rose-500",
                },
              ].map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: index % 2 === 0 ? -20 : 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.2 + index * 0.1, duration: 0.5 }}
                  className="group relative"
                >
                  <div className="bg-white/90 backdrop-blur-sm border border-amber-200/50 rounded-xl p-4 hover:bg-white transition-all duration-300 flex items-center gap-4 group-hover:scale-105">
                    <div className={`bg-gradient-to-r ${item.gradient} rounded-lg p-3 shadow-lg flex-shrink-0`}>
                      <div className="text-white">{item.icon}</div>
                    </div>
                    <h3 className="text-gray-900 font-bold text-sm lg:text-base leading-tight">{item.title}</h3>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Centered Fifth Point */}
            <div className="flex justify-center">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6, duration: 0.5 }}
                className="group relative max-w-xl"
              >
                <div className="bg-white/90 backdrop-blur-sm border border-amber-200/50 rounded-xl p-4 hover:bg-white transition-all duration-300 flex items-center gap-4 group-hover:scale-105">
                  <div className="bg-gradient-to-r from-amber-500 to-orange-500 rounded-lg p-3 shadow-lg flex-shrink-0">
                    <Award className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-gray-900 font-bold text-sm lg:text-base leading-tight">
                    Independent. Accurate. Reliable.
                  </h3>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        <Customers customerOnly={true} />

        {/* Testimonials Section - Compact */}
        <section className="py-12 bg-gradient-to-br from-indigo-50/50 via-blue-50/30 to-indigo-50/50">
          <div className="max-w-6xl mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="text-center mb-8"
            >
              <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mb-3">What Our Clients Say</h2>
              {/* <p className="text-base text-gray-600 max-w-2xl mx-auto">
                Trusted by leading investment firms, entrepreneurs, and industry professionals across India
              </p> */}
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {testimonials.map((testimonial, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1, duration: 0.4 }}
                  className="bg-gradient-to-br from-gray-50 to-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 "
                >
                  {/* <div className="flex items-center mb-3">
                    {[...Array(testimonial.rating)].map((_, i) => (
                        <Star key={i} className="w-4 h-4 text-amber-500 fill-current" />
                    ))}
                    </div> */}
                  <p className="text-base  text-gray-800 pr-4 italic">"{testimonial.content}"</p>
                  {/* <div className="border-t border-gray-200 pt-3"> */}
                  <h4 className="font-semibold text-gray-800 text-sm">{testimonial.name}</h4>
                  <p className="text-amber-600 font-medium text-xs">{testimonial.position}</p>
                  <p className="text-gray-600 text-xs">{testimonial.company}</p>
                  {/* </div> */}
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ Section - Compact */}
        <section className="py-12 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="text-center mb-8"
            >
              <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mb-3">Frequently Asked Questions</h2>
              {/* <p className="text-base text-gray-600">Get answers to common questions about our services and platform</p> */}
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-20">
              {/* LEFT COLUMN (8 FAQs) */}
              <div className="space-y-3">
                {faqs.slice(0, 8).map((faq, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 15 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05, duration: 0.4 }}
                    className="bg-white rounded-lg shadow-md overflow-hidden"
                  >
                    <button
                      onClick={() => toggleFaq(index)}
                      className="w-full px-6 py-4 text-left flex justify-between items-center hover:bg-gray-50 transition-colors duration-200"
                    >
                      <h3 className="text-base font-semibold text-gray-800 pr-4">{faq.question}</h3>
                      {openFaq === index ? (
                        <ChevronUp className="w-5 h-5 text-amber-600 flex-shrink-0" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-amber-600 flex-shrink-0" />
                      )}
                    </button>
                    {openFaq === index && (
                      <div className="px-6 pb-4">
                        <p className="text-gray-600 text-sm leading-relaxed">{faq.answer}</p>
                      </div>
                    )}
                  </motion.div>
                ))}
              </div>

              {/* RIGHT COLUMN (7 FAQs) */}
              <div className="space-y-3">
                {faqs.slice(8).map((faq, index) => (
                  <motion.div
                    key={index + 8}
                    initial={{ opacity: 0, y: 15 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: (index + 8) * 0.05, duration: 0.4 }}
                    className="bg-white rounded-lg shadow-md overflow-hidden"
                  >
                    <button
                      onClick={() => toggleFaq(index + 8)}
                      className="w-full px-6 py-4 text-left flex justify-between items-center hover:bg-gray-50 transition-colors duration-200"
                    >
                      <h3 className="text-base font-semibold text-gray-800 pr-4">{faq.question}</h3>
                      {openFaq === index + 8 ? (
                        <ChevronUp className="w-5 h-5 text-amber-600 flex-shrink-0" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-amber-600 flex-shrink-0" />
                      )}
                    </button>
                    {openFaq === index + 8 && (
                      <div className="px-6 pb-4">
                        <p className="text-gray-600 text-sm leading-relaxed">{faq.answer}</p>
                      </div>
                    )}
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="py-12 bg-gradient-to-br from-indigo-50/50 via-blue-50/30 to-indigo-50/50">
          <div className="max-w-6xl mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="text-center mb-10"
            >
              {/* <div className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-full px-4 py-2 mb-6 shadow-lg">
                <Target className="w-4 h-4" />
                <span className="text-sm font-medium">Choose Your Path</span>
              </div> */}

              <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mb-3">Select the option that best describes you to access tailored solutions and demos</h2>
              {/* <p className="text-base text-gray-600 max-w-2xl mx-auto">
                Select the option that best describes you to access tailored solutions and demos
              </p> */}
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {clientTypes.map((client, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1, duration: 0.5 }}
                  className="group relative"
                >
                  <div
                    className={`bg-gradient-to-br ${client.bgGradient} border border-white/60 rounded-xl p-6 hover:shadow-xl transition-all duration-300 hover:scale-105 h-full flex flex-col`}
                  >
                    {/* Icon */}
                    <div
                      className={`inline-flex p-3 rounded-lg bg-gradient-to-r ${client.gradient} mb-4 shadow-lg self-start`}
                    >
                      <div className="text-white">{client.icon}</div>
                    </div>

                    {/* Content */}
                    <div className="flex-grow">
                      <h3 className="text-lg font-bold text-gray-900 mb-3 leading-tight">{client.title}</h3>
                      <p className="text-gray-700 text-sm leading-relaxed mb-6">{client.description}</p>
                    </div>

                    {/* CTA Button */}
                    <a
                      href={client.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`inline-flex items-center justify-center gap-2 bg-gradient-to-r ${client.gradient} hover:bg-gradient-to-r hover:${client.hoverGradient} text-white font-semibold py-3 px-6 rounded-lg transition-all duration-300 group-hover:shadow-lg transform group-hover:-translate-y-0.5`}
                    >
                      <span className="text-sm">{client.actionText}</span>
                      <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
                    </a>
                  </div>

                  {/* Subtle glow effect on hover */}
                  <div
                    className={`absolute inset-0 bg-gradient-to-r ${client.gradient} rounded-xl opacity-0 group-hover:opacity-10 transition-opacity duration-300 -z-10 blur-xl`}
                  ></div>
                </motion.div>
              ))}
            </div>

            {/* Additional note */}
            {/* <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5, duration: 0.5 }}
              className="text-center mt-8"
            >
              <p className="text-sm text-gray-500 max-w-3xl mx-auto">
                Our team will reach out to schedule a personalized walkthrough based on your specific requirements and
                use cases.
              </p>
            </motion.div> */}
          </div>
        </section>
      </div>
      {/* Floating Request Demo Button */}
      <div className="fixed bottom-6 right-6 z-50 px-2 py-1 lg:py-2 lg:px-4 group rounded-md">
        <a
          href="/trial2.htm"
          target="_blank"
          className="inline-block px-4 py-2 bg-[#bc8b20] text-white rounded-md hover:bg-[#d6b15f] transition-colors duration-300 text-sm font-medium shadow-lg hover:shadow-xl transform hover:scale-105"
        >
          Request a Demo
        </a>
      </div>
    </Layout>
  )
}
export default Landing2
